import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { AtSign } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react';

import {
  searchEntityOptions,
  type ChatEntityChipType,
  type MentionOption,
} from './EntityChipPicker.js';

interface MentionMenuProps {
  /** Текст после `@` в последнем токене draft-а (может быть пустым). */
  query: string;
  onSelect: (option: MentionOption) => void;
  onClose: () => void;
}

function typeLabelKey(type: ChatEntityChipType): string {
  return `crm.ai_chat.context_chips.${type}`;
}

/**
 * Выпадающая подсказка сущностей при вводе `@` в композере. Повторяет паттерн
 * SlashCommandMenu: позиционирование над композером, фильтрация по запросу,
 * клавиатура ArrowUp/ArrowDown/Enter/Escape через window-слушатель.
 */
export function MentionMenu({ query, onSelect, onClose }: MentionMenuProps): ReactElement | null {
  useI18nSubscription();
  const [options, setOptions] = useState<MentionOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const optionsRef = useRef(options);
  const selectedIndexRef = useRef(selectedIndex);
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);

  useLayoutEffect(() => {
    optionsRef.current = options;
    selectedIndexRef.current = selectedIndex;
    onSelectRef.current = onSelect;
    onCloseRef.current = onClose;
  }, [options, selectedIndex, onSelect, onClose]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    void searchEntityOptions(query)
      .then((items) => {
        if (!cancelled) setOptions(items);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  useLayoutEffect(() => {
    selectedIndexRef.current = 0;
    setSelectedIndex(0);
  }, [options.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      const currentOptions = optionsRef.current;
      if (e.key === 'ArrowDown') {
        if (currentOptions.length === 0) return;
        e.preventDefault();
        const next = (selectedIndexRef.current + 1) % currentOptions.length;
        selectedIndexRef.current = next;
        setSelectedIndex(next);
      } else if (e.key === 'ArrowUp') {
        if (currentOptions.length === 0) return;
        e.preventDefault();
        const next = (selectedIndexRef.current - 1 + currentOptions.length) % currentOptions.length;
        selectedIndexRef.current = next;
        setSelectedIndex(next);
      } else if (e.key === 'Enter') {
        const selectedOption = currentOptions[selectedIndexRef.current];
        if (selectedOption) {
          e.preventDefault();
          onSelectRef.current(selectedOption);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div
      data-testid="crm-mention-menu"
      className="absolute bottom-full left-0 right-0 mb-2 z-50 rounded-xl border border-stone-200 bg-white/95 p-1.5 shadow-lg backdrop-blur-md transition-all space-y-1"
    >
      <div className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-400">
        <AtSign size={11} className="text-amber-500" />
        {i18n.t('crm.ai_chat.mention.placeholder')}
      </div>
      <div className="max-h-48 overflow-y-auto space-y-0.5 crm-microscroll">
        {isLoading ? (
          <p className="px-1 py-2 text-[11px] text-stone-400">{i18n.t('crm.ai_chat.typing')}</p>
        ) : options.length === 0 ? (
          <p className="px-1 py-2 text-[11px] text-stone-400">
            {i18n.t('crm.ai_chat.mention.empty')}
          </p>
        ) : (
          options.map((option, idx) => {
            const isSelected = idx === selectedIndex;
            return (
              <button
                key={`${option.type}:${option.id}`}
                type="button"
                data-testid={`chat-mention-option-${option.type}-${option.id}`}
                onMouseEnter={() => {
                  selectedIndexRef.current = idx;
                  setSelectedIndex(idx);
                }}
                onClick={() => onSelect(option)}
                className={`flex w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition ${
                  isSelected
                    ? 'bg-amber-50 text-amber-950 font-semibold border border-amber-200/90'
                    : 'text-stone-700 hover:bg-stone-50 border border-transparent'
                }`}
              >
                <span className="shrink-0 text-[10px] font-medium text-amber-800">
                  {i18n.t(typeLabelKey(option.type))}
                </span>
                <span className="truncate">{option.label}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
